import { createApi } from "@reduxjs/toolkit/query/react";
import { LegacyOrganizationAccount } from "../types/Account";
import { EditOrganizationAccountPayload } from "../types/Admin";
import { ListingResponse } from "../types/Listing";
import {
  SysadminLoginPayload,
  SysadminLoginResponseSuccess,
} from "../types/Onboarding";
import {
  CreateEnterprisePayload,
  EnterpriseListingPayload,
  SingleEnterprisePayload,
  SingleEnterpriseResponse,
  TEnterprise,
} from "../types/Sysadmin";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

export enum SysadminApiTags {
  ENTERPRISE_CREATED = "ENTERPRISE_CREATED",
  ENTERPRISE_MODIFIED = "ENTERPRISE_MODIFIED",
  ENTERPRISE_DELETED = "ENTERPRISE_DELETED",
  ENTERPRISE_SINGLE = "ENTERPRISE_SINGLE",
  ENTERPRISE = "ENTERPRISE",
  AVAILABLE_CHILD_ACCOUNTS = "AVAILABLE_CHILD_ACCOUNTS",
}

export const SysadminAPIs = createApi({
  reducerPath: "sysadminAPI",
  tagTypes: [
    SysadminApiTags.ENTERPRISE_CREATED,
    SysadminApiTags.ENTERPRISE_MODIFIED,
    SysadminApiTags.ENTERPRISE_DELETED,
    SysadminApiTags.ENTERPRISE_SINGLE,
    SysadminApiTags.ENTERPRISE,
    SysadminApiTags.AVAILABLE_CHILD_ACCOUNTS,
  ],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(appBaseQuery.withAuth, args, api, extraOptions),
  endpoints: (builder) => ({
    // create enterprise
    createEnterprise: builder.mutation<TEnterprise, CreateEnterprisePayload>({
      query: (params: CreateEnterprisePayload) => {
        return {
          url: API_ENDPOINTS.SYSADMIN.ENTERPRISE_ACCOUNTS,
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [SysadminApiTags.ENTERPRISE_CREATED],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TEnterprise) => {
        return response;
      },
    }),

    // list enterprises
    listEnterprises: builder.query<
      ListingResponse<TEnterprise[]>,
      EnterpriseListingPayload
    >({
      query: (params: EnterpriseListingPayload) => {
        return {
          url: API_ENDPOINTS.SYSADMIN.ENTERPRISE_ACCOUNTS,
          params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        SysadminApiTags.ENTERPRISE_CREATED,
        SysadminApiTags.ENTERPRISE_MODIFIED,
        SysadminApiTags.ENTERPRISE_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TEnterprise[]>) => {
        return response;
      },
    }),

    // single enterprise
    singleEnterprise: builder.query<
      SingleEnterpriseResponse,
      SingleEnterprisePayload
    >({
      query: ({ enterprise_id }) => {
        return {
          url: API_ENDPOINTS.SYSADMIN.SINGLE_ENTERPRISE_ACCOUNT(enterprise_id),
          method: API_METHODS.GET,
        };
      },
      providesTags: (result, error, arg) => [
        SysadminApiTags.ENTERPRISE_SINGLE,
        { type: SysadminApiTags.ENTERPRISE as const, id: arg.enterprise_id },
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: SingleEnterpriseResponse) => {
        return response;
      },
    }),

    // edit enterprise
    editEnterprise: builder.mutation<
      LegacyOrganizationAccount,
      EditOrganizationAccountPayload
    >({
      // TODO: change to legacy incomplete
      query: ({ account_id, data }) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_ACCOUNT(account_id),
          method: API_METHODS.PATCH,
          body: data,
        };
      },
      invalidatesTags: [
        SysadminApiTags.ENTERPRISE_MODIFIED,
        SysadminApiTags.ENTERPRISE_SINGLE,
        SysadminApiTags.AVAILABLE_CHILD_ACCOUNTS,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: LegacyOrganizationAccount) => {
        // TODO: change to legacy incomplete
        return response;
      },
    }),

    // delete single user
    // deleteSingleUser: builder.mutation<void, DeleteOrganizationUserPayload>({
    //   query: ({ email }) => {
    //     return {
    //       url: API_ENDPOINTS.ADMINS.DELETE_ORGANIZATION_USER(email),
    //       method: API_METHODS.DELETE,
    //     };
    //   },
    //   invalidatesTags: [SysadminApiTags.USER_DELETED],
    //   transformErrorResponse(baseQueryReturnValue) {
    //     handleAuthErrorCode(baseQueryReturnValue);
    //     return baseQueryReturnValue;
    //   },
    //   transformResponse: (response: void) => {
    //     return response;
    //   },
    // }),

    //Sysadmin Login As Account User
    sysadminLoginAsAccountUser: builder.mutation<
      SysadminLoginResponseSuccess,
      SysadminLoginPayload
    >({
      query: (params: SysadminLoginPayload) => {
        return {
          url: API_ENDPOINTS.SYSADMIN.SYSADMIN_LOGIN_AS_ACCOUNT_USER,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (response: SysadminLoginResponseSuccess) => {
        return response;
      },
    }),

    getAvailableChildAccounts: builder.query<any, string>({
      query: (account_id) => {
        return {
          url: API_ENDPOINTS.SYSADMIN.AVAILABLE_CHILD_ACCOUNTS(account_id),
          method: API_METHODS.GET,
        };
      },
      providesTags: [SysadminApiTags.AVAILABLE_CHILD_ACCOUNTS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
    }),
  }),
});

export const {
  // enterprise
  useCreateEnterpriseMutation,
  useListEnterprisesQuery,
  useSingleEnterpriseQuery,
  useEditEnterpriseMutation,
  useSysadminLoginAsAccountUserMutation,
  useGetAvailableChildAccountsQuery,
  // useDeleteSingleUserMutation,
} = SysadminAPIs;
